import { IActiveJob, IActiveJobs } from '@th-types/active-jobs.type';
import {
  DropdownListItem,
  DropdownListItemEmpty,
} from '@components/elements/DropdownList';
import { useActiveJobsByWorker } from './useActiveJobsByWorker';

export interface FetchActiveJobsListParams {
  shareJobToWorker: (jobId: number, workerId: number) => Promise<void>;
}

export function useActiveJobsList(workerId: number) {
  const {
    data: activeJobsData,
    isLoading,
    error,
  } = useActiveJobsByWorker({
    workerId,
  });

  const getJobLabel = (job: IActiveJob) =>
    `${job.position} - ${job.address?.city}/${
      job.address?.state.ansiAbbreviation ?? ''
    }`;

  const getJobKey = (job: IActiveJob) =>
    `${job.position} ${job.address?.city} ${
      job.address?.state.ansiAbbreviation ?? ''
    }`;

  const getShareableJobs = (
    shareJobToWorker: (jobId: number, workerId: number) => Promise<void>
  ): DropdownListItem[] | DropdownListItemEmpty => {
    if (isLoading) {
      return {
        label: 'Loading available jobs...',
      } as DropdownListItemEmpty;
    }

    if (error) {
      return {
        label: 'Failed to load available jobs',
      } as DropdownListItemEmpty;
    }

    const activeJobs = (activeJobsData as any)?.results;
    if (!activeJobs || activeJobs.length === 0) {
      return {
        label: 'No published jobs to share',
      } as DropdownListItemEmpty;
    }

    // Filter out jobs that can't be shared (already have jobAssignment)
    const shareableJobs = activeJobs.filter(
      ({ jobAssignment }: { jobAssignment: any }) => !jobAssignment
    );

    if (shareableJobs.length === 0) {
      return {
        label: 'No available jobs to share',
      } as DropdownListItemEmpty;
    }

    // Group jobs by eventName
    const jobsByEvent = shareableJobs.reduce(
      (acc: Record<string, IActiveJob[]>, { job }: IActiveJobs) => {
        const { eventName } = job;
        if (!acc[eventName]) {
          acc[eventName] = [];
        }
        acc[eventName].push(job);
        return acc;
      },
      {} as Record<string, IActiveJob[]>
    );

    // Create dropdown items with grouped structure
    const dropdownItems: DropdownListItem[] = [];

    Object.entries(jobsByEvent).forEach(([eventName, jobs]) => {
      // Add header for the event (job)
      dropdownItems.push({
        header: eventName,
        label: '', // Empty label for header-only items
        key: `header-${eventName}`,
        id: 0, // No action for headers
        action: null,
      });

      // Add each position under the event
      (jobs as IActiveJob[]).forEach((job: IActiveJob) => {
        dropdownItems.push({
          header: undefined, // No header for position items
          label: getJobLabel(job),
          key: getJobKey(job),
          id: job.id,
          action: () => shareJobToWorker(job.id, workerId),
        });
      });
    });

    return dropdownItems;
  };

  return {
    getShareableJobs,
    isLoading,
    error,
  };
}

// Legacy function for backward compatibility - not used anymore
export default function useFetchJobList() {
  return () => {
    // This is a legacy function that's no longer used
    // The new approach uses useActiveJobsList directly
    throw new Error(
      'useFetchJobList is deprecated. Use useActiveJobsList instead.'
    );
  };
}
